import clsx from 'clsx';
import { StoreApi } from 'zustand';
import { UseBoundStore } from 'zustand/react';
import { ReactProductState } from './product';

const product_data__hide_oos_variants = true;

interface Props {
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
}

const DESKTOP_COLS_CLASSES = {
  "3": "md:grid-cols-3",
  "4": "md:grid-cols-4",
  "5": "md:grid-cols-5",
  "6": "md:grid-cols-6",
};
const MOBILE_COLS_CLASSES = {
  "1": "grid-cols-1",
  "2": "grid-cols-2",
  "3": "grid-cols-3",
  "4": "grid-cols-4",
};

const getGridClasses = (desktopCols?: string, mobileCols?: string) => {
  const desktop = DESKTOP_COLS_CLASSES[desktopCols || "5"] || "md:grid-cols-5";
  const mobile = MOBILE_COLS_CLASSES[mobileCols || "2"] || "grid-cols-2";
  return `mt-2 grid ${desktop} ${mobile} gap-4`;
};

// TODO: do not pass useProduct, pass all props.
export const ProductGroupVariantSelector = ({ useProduct, gridColumnsDesktop, gridColumnsMobile }: Props) => {
  const {
    product,
    selectedVariant,
    resetSelectedConfigurableProducts,
    resetSelectedProductExtraOptions,
    setOptionsVariant,
    options
  } = useProduct(({
    product,
    selectedVariant,
    resetSelectedConfigurableProducts,
    resetSelectedProductExtraOptions,
    setOptionsVariant,
    formId,
    options
  }) => ({
    product,
    selectedVariant,
    resetSelectedConfigurableProducts,
    resetSelectedProductExtraOptions,
    setOptionsVariant,
    formId,
    options
  }));

  return (
    <div className={"flex flex-col gap-2"}>
      <div className="grid gap-2">
        {product?.variants?.filter((variant) => !product_data__hide_oos_variants || variant.available)
          ?.length <= 0 ||
          (product?.variants?.length === 1 && product?.variants?.[0]?.option1 === "Default Title")
          ? null
          : <>
            {product?.options_with_values?.map(({ name, position, values },) => {
              const index = position - 1;
              return (
                <div key={name} className="relative">
                  <h3 className="text-sm py-2">{name}</h3>
                  <fieldset className={getGridClasses(gridColumnsDesktop, gridColumnsMobile)}>
                    {values.map((value) => {
                      const notAvailable = !product?.variants?.some((variant) => {
                        switch (index) {
                          case 0: {
                            return variant.options[index] === value && variant.available;
                          }
                          case 1: {
                            return (
                              variant.options[0] === options[0] &&
                              variant.options[index] === value &&
                              variant.available
                            );
                          }
                          case 2: {
                            return (
                              variant.options[0] === options[0] &&
                              variant.options[1] === options[1] &&
                              variant.options[index] === value &&
                              variant.available
                            );
                          }
                        }
                        return false;
                      });

                      const unavailable = !product?.variants?.some(
                        (variant) => variant.options[index] === value && variant.available
                      );


                      if (product_data__hide_oos_variants && (unavailable || notAvailable)) {
                        return null;
                      }
                      
                      // Build the alt string based on the option name.
                      // For "Bowl Position" we combine the "Vanity Top" selection with the bowl value.
                      let altString;
                      if (name === "Bowl Position") {
                        altString = `Vanity Top ${selectedVariant.option1} ${name} ${value}`;
                      } else {
                        altString = `${name} ${value}`;
                      }

                      // Determine which image to render.
                      // For "Vanity Top" and "Bowl Position", we try to find the most specific (conditional) image.
                      // For "Interior", we require an exact match.
                      let imageToRender = null;
                      const option1Name = product?.options_with_values[0].name;
                      if (name === option1Name || name === "Bowl Position") {
                        const potentialImages = product.media?.reduce(
                          (result, media) => {
                            if (!media.alt) return result;
                            const isExactMatch = media.alt === altString;
                            const isPartialMatch = media.alt.includes(altString);

                            if (isExactMatch) {
                              return { exactMatch: media, bestMatch: result.bestMatch };
                            }
                            if (isPartialMatch) {
                              if (
                                !result.bestMatch ||
                                media.alt.length > result.bestMatch.alt.length
                              ) {
                                return { exactMatch: result.exactMatch, bestMatch: media };
                              }
                            }
                            return result;
                          },
                          { exactMatch: null, bestMatch: null }
                        );
                        imageToRender =
                          potentialImages.exactMatch || potentialImages.bestMatch;
                      } else if (name === "Interior") {
                        // For "Interior", we want an exact match (e.g., "Interior Lite")
                        imageToRender = product.media?.find(
                          (media) => media.alt === altString
                        );
                      }


                      let extraPrice = null;
                      if (name === "Interior") {

                        const [option1, option2] = options;
                        const variantsRelated = product.variants.filter((v) => {
                          return v.options?.includes(option1) && v.options?.includes(option2);
                        });

                        const variantRelated = variantsRelated.find((v) => v.option3 === value);
                        const comparedVariant = variantsRelated.find((v) => v.option3 !== value);
                        extraPrice = variantsRelated && variantRelated && comparedVariant && variantRelated.price > comparedVariant.price ? variantRelated.price - product.price : null;
                      }

                      return (
                        <button
                          key={value}
                          type={"button"}
                          className="cursor-pointer select-none" 
                          onClick={() => {
                          setOptionsVariant(index, value);
                          resetSelectedConfigurableProducts();
                          resetSelectedProductExtraOptions();
                        }}>
                          <span
                            className={clsx(
                              "flex flex-col gap-2 text-center items-center",
                              notAvailable && "variant-not-available"
                            )}
                          >
                            <div className={`relative overflow-hidden aspect-1 w-full bg-white rounded-lg border-solid ${ options[index] === value ? "border-primary-bg border-[3px]" : "border-gray-300 border-[1px]"}`}>
                              {imageToRender && <img className={`absolute left-0 top-0 bottom-0 right-0 w-full h-full ${name === "Interior" ? "object-cover" : "object-contain" }`} width={200} height={200} src={imageToRender.src} alt={imageToRender.alt} />}
                            </div>
                            { name === "Interior" ? <p className={"text-sm"}>{value} {extraPrice && extraPrice > 0 ? `+${window.formatMoney(extraPrice)}` : null}</p> : <p className={"text-sm"}>{value}</p>}
                            
                          </span>
                        </button>
                      );
                    })}
                  </fieldset>
                </div>
              );
            })}
          </>}
      </div>
    </div>
  )
}
